<script>
	import { onMount, tick } from "svelte";
	import Hydrator from "@reperio-news/core.components/Hydrator.svelte";

	export let items = [];
	export let active = null;

	let nav;
	let measurements;
	let moreButton;
	let visibleCount = null;
	let more;
	let observer;
	$: visible = visibleCount === null ? items.length : Math.min(visibleCount, items.length);
	$: overflow = items.slice(visible);
	$: overflowActive = overflow.some((item) => item.url === active);
	// CMS previews can change labels or add items without remounting the component.
	$: if (observer && items) refreshMeasurements();

	function measure() {
		const width = nav.getBoundingClientRect().width;
		if (!width) {
			// Do not reopen a stale dropdown when returning from the mobile drawer.
			more.open = false;
			return;
		}
		const links = [...measurements.children];
		const gap = parseFloat(getComputedStyle(measurements).columnGap) || 0;
		const widths = links.map((link) => link.getBoundingClientRect().width);
		const total = widths.reduce((sum, value) => sum + value, 0) + gap * Math.max(0, widths.length - 1);
		let count = widths.length;
		if (total > width) {
			const available = width - moreButton.getBoundingClientRect().width - gap;
			let used = 0;
			count = 0;
			for (const itemWidth of widths) {
				const next = used + (count ? gap : 0) + itemWidth;
				if (next > available) break;
				used = next;
				count++;
			}
		}
		if (count !== visibleCount) {
			// A resize can move the focused link or hide the More control itself.
			const focused = document.activeElement;
			if (nav.contains(focused)) {
				const href = focused.getAttribute("href");
				tick().then(() => {
					if (!nav?.isConnected) return;
					const rowLinks = [...nav.querySelectorAll(".af-nav__links a")];
					const target = rowLinks.find((link) => href && link.getAttribute("href") === href);
					(target || (count < items.length ? moreButton : rowLinks[rowLinks.length - 1]))?.focus();
				});
			}
			more.open = false;
			visibleCount = count;
		}
	}

	async function refreshMeasurements() {
		await tick();
		if (!observer) return;
		observer.disconnect();
		observer.observe(nav);
		observer.observe(measurements);
		observer.observe(moreButton);
		measure();
	}

	onMount(() => {
		observer = new ResizeObserver(measure);
		return () => {
			observer.disconnect();
			observer = null;
		};
	});

	function dismiss(event) {
		if (more?.open && !nav.contains(event.target)) more.open = false;
	}

	function keydown(event) {
		if (more?.open && event.key === "Escape") {
			more.open = false;
			moreButton.focus();
			event.preventDefault();
		}
	}
</script>

<svelte:window on:pointerdown={dismiss} on:focusin={dismiss} on:keydown={keydown} />

<Hydrator component="DesktopNav" props={{ items, active }} hydrate={!__ADMIN__}>
	<nav class="af-header__nav" class:af-nav--ready={visibleCount !== null} aria-label="Secțiuni" bind:this={nav}>
		<ul class="af-nav__links">
			{#each items.slice(0, visible) as item}
				<li><a class="af-nav__link" href={item.url} target={item.target || undefined} class:is-active={active === item.url} aria-current={active === item.url ? "page" : undefined}>{item.name}</a></li>
			{/each}
		</ul>
		<details class="af-nav__more" class:af-nav__more--hidden={!overflow.length} bind:this={more}>
			<summary bind:this={moreButton} class:af-nav__current={overflowActive} tabindex={overflow.length ? 0 : -1} aria-hidden={!overflow.length ? "true" : undefined}>
				Mai multe
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
			</summary>
			<ul class="af-nav__panel">
				{#each overflow as item}
					<li><a href={item.url} target={item.target || undefined} class:is-active={active === item.url} aria-current={active === item.url ? "page" : undefined}>{item.name}</a></li>
				{/each}
			</ul>
		</details>
		<!-- Measure every label even when it lives in the dropdown; no duplicate links. -->
		<div class="af-nav__measure" aria-hidden="true" inert>
			<ul bind:this={measurements}>
				{#each items as item}<li><span class="af-nav__link">{item.name}</span></li>{/each}
			</ul>
		</div>
	</nav>
</Hydrator>

<style lang="scss">
	.af-header__nav {
		position: relative;
		display: flex;
		align-items: flex-start;
		gap: var(--af-space-2);
		min-width: 0;
	}

	ul {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	.af-nav__links,
	.af-nav__measure ul {
		display: flex;
		flex-wrap: nowrap;
		gap: var(--af-space-2);
		li { flex: 0 0 auto; }
		li:first-child .af-nav__link { padding-left: 0; }
	}

	.af-nav__links {
		min-width: 0;
		// Usable, single-line fallback before JS runs or when it is disabled.
		overflow-x: auto;
	}

	.af-nav--ready .af-nav__links { overflow: hidden; }

	.af-nav__link,
	summary {
		display: block;
		padding: var(--af-space-3);
		font: 500 var(--af-text-ui) / 1 var(--af-font-sans);
		color: var(--af-ink-muted);
		border-bottom: 2px solid transparent;
		white-space: nowrap;
		transition: color 0.15s var(--af-ease-out);
	}

	a:hover, summary:hover { color: var(--af-ink); }
	a:focus-visible, summary:focus-visible { outline: 2px solid var(--af-focus); outline-offset: -2px; }
	a[aria-current="page"], summary.af-nav__current {
		color: var(--af-ink);
		border-bottom-color: var(--af-brand);
	}

	.af-nav__more {
		flex: 0 0 auto;
		margin-left: auto;
	}

	.af-nav__more--hidden {
		position: absolute;
		right: 0;
		visibility: hidden;
	}

	summary {
		display: flex;
		align-items: center;
		gap: var(--af-space-2);
		list-style: none;
		cursor: pointer;
		&::-webkit-details-marker { display: none; }
	}

	details[open] summary svg { transform: rotate(180deg); }

	.af-nav__panel {
		position: absolute;
		right: 0;
		top: 100%;
		z-index: 30;
		width: max-content;
		min-width: min(14rem, 100%);
		max-width: 100%;
		max-height: min(60vh, 32rem);
		overflow-y: auto;
		padding: var(--af-space-2);
		border: 1px solid var(--af-hairline);
		border-radius: 0 0 var(--af-radius-card) var(--af-radius-card);
		background: var(--af-paper);
		box-shadow: var(--af-shadow-card);

		a {
			display: block;
			padding: var(--af-space-3);
			font: 500 var(--af-text-ui) / var(--af-leading-ui) var(--af-font-sans);
			color: var(--af-ink-muted);
			overflow-wrap: anywhere;
			&:hover { background: var(--af-surface-sunk); color: var(--af-ink); }
			&[aria-current="page"] { color: var(--af-brand); background: var(--af-surface-sunk); }
		}
	}

	.af-nav__measure {
		position: absolute;
		width: 100%;
		height: 0;
		overflow: hidden;
		visibility: hidden;
		pointer-events: none;
		ul { width: max-content; }
	}
</style>
